import React, { useLayoutEffect, useRef } from 'react';
import { gsap, Linear } from 'gsap';
import { useIsMounted } from '@hooks/useIsMounted';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import ScrollMagic from 'scrollmagic';
import { Columns } from '@components/Columns';
import { SpacingStack } from '@components/SpacingStack';
import { CloudImage } from '@components/CloudImage';
import { Box } from '@components/Box';
import {
  MultipleInvitesWrapper,
  MultipleRsvpColumnWrapper,
  MultipleRsvpTextPairingWrapper,
  SendRSVPTextWrapper,
  StyledBox,
} from './MultipleRsvpInvites.styles';
import { onlineRsvpPageData } from '@data-ts/pages/online-rsvp';
import { cloudAssetUrl } from '@utils/cloudAssetUrl.util';
import { useLottieAnimation } from '@hooks/useLottieAnimation';

const multipleInvitesData = onlineRsvpPageData.multipleInvites;

export const MultipleRsvpInvites: React.FC<{}> = () => {
  const groupRsvpRef = useRef<HTMLDivElement>(null);
  const scrollController = useScrollController();
  const isMounted = useIsMounted();
  const { animationRef } = useLottieAnimation(
    {
      path: cloudAssetUrl('wedding-invitations/group-rsvp/bodymovin/data.json'), // Required
      renderer: 'svg', // Required
      loop: false, // Optional
      autoplay: false, // Optional
      name: 'guestlist features',
      rendererSettings: {
        progressiveLoad: false,
        preserveAspectRatio: 'xMidYMid slice',
      },
    },
    { containerRef: groupRsvpRef, isLoadInitially: true }
  );

  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let livestreamTimeline: ReturnType<typeof gsap.timeline>;

    if (scrollController && isMounted) {
      const initTimeline = () => {
        if (scrollController) {
          livestreamTimeline = gsap.timeline({ paused: true });
          livestreamTimeline.to(
            { frame: 0 },
            1,
            {
              frame: 1,
              onUpdate: () => {
                animationRef.current?.goToAndStop(Math.round(livestreamTimeline.totalProgress() * 250), true);
              },
              ease: Linear.easeNone,
            },
            0
          );
        }
      };
      const initScene = () => {
        scene = new ScrollMagic.Scene({
          triggerElement: '#MultipleRsvpInvites',
          triggerHook: 1,
          offset: -300,
          duration: '100%',
        })
          .addTo(scrollController)
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            livestreamTimeline?.progress(event.progress);
          });
      };
      initTimeline();
      initScene();
    }
    const clearScene = () => {
      if (livestreamTimeline) {
        livestreamTimeline.progress(0);
        livestreamTimeline.kill();
      }
      if (scene) {
        scene.destroy(true);
      }
    };
    return clearScene;
  }, [scrollController, isMounted, animationRef]);

  return (
    <MultipleInvitesWrapper
      paddingTop={[5, 5, 12]}
      backgroundColor="yellow1"
      backgroundImage="wedding-invitations/multi-event-rsvp/multi-event-rsvp_bg1.jpg"
    >
      <SpacingStack spacing={[]}>
        <div>
          <Columns>
            <Columns.Column width="half">
              <Box marginLeft={[0, -12, -12]} marginTop={[-8, -15, -15]} marginBottom={[12, 12, 12]}>
                <div style={{ width: '100%' }} ref={groupRsvpRef} />
              </Box>
            </Columns.Column>
            <Columns.Column width="half">
              <Box paddingX={[7]}>
                <SendRSVPTextWrapper
                  alignment="left"
                  eyebrow={multipleInvitesData.group.eyebrow}
                  eyebrowColor="blue5"
                  eyebrowVariant="sectionEyeBrow24"
                  eyebrowTagName="h3"
                  title={multipleInvitesData.group.title}
                  titleColor="blue6"
                  titleVariant="hed56"
                  body={multipleInvitesData.group.content}
                  bodyVariant="dek24"
                  bodyColor="blue6"
                />
              </Box>
            </Columns.Column>
          </Columns>
        </div>
        <StyledBox marginBottom={[6, 6, 13]} id="MultipleRsvpInvites">
          <Columns centered={true}>
            <MultipleRsvpColumnWrapper width={8}>
              <Columns.Column width={5}>
                <Box marginTop={[7, 7, 14]} paddingX={[7]}>
                  <MultipleRsvpTextPairingWrapper
                    alignment="left"
                    eyebrow={multipleInvitesData.multi.eyebrow}
                    eyebrowColor="blue5"
                    eyebrowVariant="sectionEyeBrow24"
                    eyebrowTagName="h3"
                    title={multipleInvitesData.multi.title}
                    titleColor="blue6"
                    titleVariant="hed56"
                    body={multipleInvitesData.multi.content}
                    bodyVariant="dek24"
                    bodyColor="blue6"
                  />
                </Box>
              </Columns.Column>
              <Columns.Column width={7}>
                <Box paddingTop={[7, 0, 0]}>
                  <CloudImage alt="" src={'wedding-invitations/multi_event_rsvp/multi-event-rsvp.svg'} width={'100%'} />
                </Box>
              </Columns.Column>
            </MultipleRsvpColumnWrapper>
          </Columns>
        </StyledBox>
      </SpacingStack>
    </MultipleInvitesWrapper>
  );
};
